'use client';

import { useEffect, useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { partsApi, inventoryApi, type Part, type InventoryLog } from '@parts/api';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  Tag,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  QrCode,
  Barcode,
  MapPin,
  Calendar,
  User,
  RefreshCw,
  Clock,
  FileText,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';

export default function PartDetailPage() {
  const router = useRouter();
  const params = useParams();
  const { t, locale } = useTranslation();
  const confirm = useConfirm();
  const partId = params.id as string;

  const [part, setPart] = useState<Part | null>(null);
  const [inventoryLogs, setInventoryLogs] = useState<InventoryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    loadPartDetails();
    loadInventoryLogs();
  }, [partId]);

  const loadPartDetails = async () => {
    try {
      setLoading(true);
      const response = await partsApi.get(partId);
      setPart(response);
    } catch (error) {
      console.error('Failed to load part:', error);
      toast.error(t.parts.loadPartFailed);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await inventoryApi.getLogs({
        partId,
        limit: 20,
      });
      setInventoryLogs(response.items);
    } catch (error) {
      console.error('Failed to load inventory logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t.parts.confirmDeletePart,
      variant: 'danger',
    });
    if (!confirmed) return;
    
    try {
      await partsApi.delete(partId);
      toast.success(t.parts.deleteSuccess);
      router.push('/parts/manage');
    } catch (error: any) {
      console.error('Failed to delete part:', error);
      toast.error(error.response?.data?.error?.message || t.parts.deleteFailed);
    }
  };

  const getStockStatusBadge = () => {
    if (!part) return null;
    
    const statusConfig = {
      OUT_OF_STOCK: { bg: 'bg-red-100', text: 'text-red-700', label: t.parts.statusOut },
      LOW: { bg: 'bg-orange-100', text: 'text-orange-700', label: t.parts.statusLow },
      NORMAL: { bg: 'bg-green-100', text: 'text-green-700', label: t.parts.statusNormal },
    };

    const config = statusConfig[part.stockStatus];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getOperationTypeIcon = (type: string) => {
    if (type.includes('IN') || type === 'RECEIVING') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (type.includes('OUT')) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <RefreshCw className="w-4 h-4 text-blue-600" />;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!part) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center">
        <Package className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-500">{t.parts.partNotFound}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => router.push('/parts/manage')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.parts.backToList}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      {/* 顶部操作栏 */}
      <div className="bg-white border-b">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/parts/manage')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {part.partNumber}
                </h1>
                <p className="text-gray-500">
                  {locale === 'zh' ? (part.partNameCn || part.partNameEn) : part.partNameEn}
                </p>
              </div>
              {getStockStatusBadge()}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.push(`/parts/${partId}/edit`)}
              >
                <Edit className="w-4 h-4 mr-2" />
                {t.common.edit}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.push(`/parts/labels?parts=${partId}`)}
              >
                <Tag className="w-4 h-4 mr-2" />
                {t.parts.printLabel}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t.common.delete}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：基本信息和库存信息 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 基本信息 */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">{t.parts.basicInformation}</h2>
                </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-2 gap-6">
                  <InfoItem label={t.parts.partNumberLabel} value={part.partNumber} icon={<FileText className="w-4 h-4" />} />
                  <InfoItem label={t.parts.partNameLabel} value={part.partNameEn} />
                  {part.partNameCn && <InfoItem label={t.parts.partNameCnLabel} value={part.partNameCn} />}
                  {part.specifications && <InfoItem label={t.parts.specificationsLabel} value={part.specifications} />}
                  {part.description && <InfoItem label={t.parts.description} value={part.description} className="col-span-2" />}
                  <InfoItem label={t.parts.unit} value={part.unit} />
                  <InfoItem label={t.parts.status} value={part.status === 'ACTIVE' ? t.parts.statusActive : t.parts.statusInactive} />
                  {part.category && <InfoItem label={t.parts.category} value={part.category} />}
                  {part.supplier && <InfoItem label={t.parts.supplier} value={part.supplier} />}
                  {part.supplierPartNumber && <InfoItem label={t.parts.supplierPartNumber} value={part.supplierPartNumber} />}
                  {part.unitPrice && (
                    <InfoItem 
                      label={t.parts.unitPrice} 
                      value={`${part.unitPrice} ${part.currency || 'CNY'}`} 
                    />
                  )}
                  {part.leadTime && <InfoItem label={t.parts.leadTime} value={part.leadTime.toString()} />}
                </div>
                  </div>
                  </div>

            {/* 库存信息 */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">{t.parts.stockInformation}</h2>
                  </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <StockCard
                    label={t.parts.currentStock}
                    value={`${part.currentStock} ${part.unit}`}
                    color="blue"
                  />
                  <StockCard
                    label={t.parts.minStock}
                    value={`${part.minStock} ${part.unit}`}
                    color="orange"
                  />
                  {part.maxStock && (
                    <StockCard
                      label={t.parts.maxStock}
                      value={`${part.maxStock} ${part.unit}`}
                      color="green"
                    />
                  )}
                  <StockCard
                    label={t.parts.stockStatus}
                    value={getStockStatusBadge() || '-'}
                    color="gray"
                  />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  {part.station && <InfoItem label={t.parts.station} value={part.station} icon={<MapPin className="w-4 h-4" />} />}
                  {part.warehouseLocation && <InfoItem label={t.parts.warehouseLocation} value={part.warehouseLocation} icon={<MapPin className="w-4 h-4" />} />}
                </div>
                </div>
            </div>


            {/* 操作历史 */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">{t.parts.operationHistory}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadInventoryLogs}
                  disabled={logsLoading}
                >
                  <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              <div className="divide-y">
                {logsLoading ? (
                  <div className="p-8 text-center text-gray-500">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin text-gray-400" />
                    <p>{t.common.loading}</p>
                  </div>
                ) : inventoryLogs.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>{t.parts.noOperationRecords}</p>
                  </div>
                ) : (
                  inventoryLogs.map((log) => (
                    <div key={log.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getOperationTypeIcon(log.operationType)}
                        <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{log.operationType}</span>
                              <span className="text-sm text-gray-500">
                                {log.quantity > 0 ? '+' : ''}{log.quantity} {part.unit}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {t.parts.stockLabel}: {log.previousStock} → {log.newStock} {part.unit}
                            </div>
                            {log.reason && (
                              <div className="text-sm text-gray-500 mt-1">
                                {t.parts.reason}: {log.reason}
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {log.operatorName}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDateTime(log.createdAt)}
                              </span>
                              {log.station && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {log.station}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* 右侧：元数据和快速操作 */}
          <div className="space-y-6">
            {/* 快速操作 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.parts.quickActions}</h2>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => router.push(`/parts/inventory-ops?code=${part.partNumber}`)}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t.inventory.checkIn}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => router.push(`/parts/inventory-ops?code=${part.partNumber}&mode=checkout`)}
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  {t.inventory.checkOut}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => router.push(`/parts/labels?parts=${partId}`)}
                >
                  <Tag className="w-4 h-4 mr-2" />
                  {t.parts.printLabel}
                </Button>
              </div>
            </div>

            {/* 元数据 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.parts.recordInfo}</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t.parts.createdAt}</span>
                  <span className="text-gray-900">{formatDateTime(part.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t.parts.updatedAt}</span>
                  <span className="text-gray-900">{formatDateTime(part.updatedAt)}</span>
                </div>
                {part.createdBy && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t.parts.createdBy}</span>
                    <span className="text-gray-900">{part.createdBy}</span>
                  </div>
                )}
                {part.updatedBy && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t.parts.updatedBy}</span>
                    <span className="text-gray-900">{part.updatedBy}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 关联分组 */}
            {part.groups && part.groups.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.parts.associatedGroups}</h2>
                <div className="space-y-2">
                  {part.groups.map((item) => (
                    <div
                      key={item.groupId}
                      className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md text-sm"
                    >
                      {item.group.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function InfoItem({ 
  label, 
  value, 
  icon, 
  className = '' 
}: { 
  label: string; 
  value: string; 
  icon?: React.ReactNode; 
  className?: string;
}) {
  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}

function StockCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string | React.ReactNode;
  color: 'blue' | 'orange' | 'green' | 'gray';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    orange: 'bg-orange-50 border-orange-200',
    green: 'bg-green-50 border-green-200',
    gray: 'bg-gray-50 border-gray-200',
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="text-xs text-gray-600 mb-1">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}
